import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Empty } from "antd";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { httpClient } from "../api/httpClient";
import { ConnectionStatusBadge } from "./status_badges.component";
import { Endpoints } from "../constants/endpoint.constant";
import { formatSpeed } from "../services/firestoreTracking.service";
import { TripStatus } from "../constants/general.constant";
import {
  buildCleanRoutePath,
  buildCleanRouteSegments,
  buildSolidRoadRouteSegments,
  distanceMeters,
  hasRouteJoinLongerThan,
  splitRouteAtSelfIntersections,
} from "../utils/routePath.util";

const defaultCenter = {
  lat: -26.204103,
  lng: 28.047305,
};
const LIVE_TRACKING_ZOOM = 18;
const CONTEXT_ZOOM = 12;
const MIN_ZOOM = 3;
const MAX_ZOOM = 21;
const MIN_ROUTE_BOUND_PADDING_DEGREES = 0.35;
const MAX_REASONABLE_ROUTE_PADDING_DEGREES = 2.5;
const ROUTE_NORMAL_COLOR = "#16a34a";
const ROUTE_SPEEDING_COLOR = "#e15631";
const ROUTE_OFFLINE_COLOR = "#6b53d5";
const ROUTE_DOT_MATCH_METERS = 140;
const ROUTE_CONNECTOR_MAX_METERS = 220;
const ROUTE_GAP_CONNECTOR_MAX_METERS = 700;
const SAVED_ROUTE_MAX_JOIN_METERS = 15_000;
const SAVED_ROUTE_SAMPLE_FALLBACK_JOIN_METERS = 1_500;
const ROUTED_GAP_MIN_METERS = ROUTE_GAP_CONNECTOR_MAX_METERS;
const ROUTED_GAP_MAX_METERS = 30_000;
const TRACKING_DESTINATION_MATCH_METERS = 2_000;
const GPS_SAMPLE_LAG_WARNING_MS = 2 * 60 * 1000;

function formatHeartbeatAge(value) {
  const ageMs = Number(value);
  if (!Number.isFinite(ageMs)) return "No heartbeat";
  const seconds = Math.max(0, Math.round(ageMs / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

function formatHeartbeatState(tracking) {
  if (!tracking?.lastTelemetryAtMs) return "No server upload yet";
  if (tracking?.isTrackingTerminal || tracking?.connectionStatus === "ended") return "Tracking ended";
  if (tracking?.trackingStatusKey === "tracking_paused") return "Tracking paused";
  return formatHeartbeatAge(tracking?.heartbeatAgeMs);
}

function latestSampleTimestampMs(samples = []) {
  return (Array.isArray(samples) ? samples : []).reduce((latest, sample) => {
    const value = Number(sample?.sortAtMs || sample?.recordedAtMs || sample?.createdAtMs || 0);
    return Number.isFinite(value) && value > latest ? value : latest;
  }, 0);
}

function latestRecordTimestampMs(records = [], fields = []) {
  return (Array.isArray(records) ? records : []).reduce((latest, record) => {
    const value = fields.reduce((resolved, field) => {
      if (resolved != null && resolved !== "" && Number.isFinite(Number(resolved))) return resolved;
      return record?.[field];
    }, null);
    const number = Number(value || 0);
    return Number.isFinite(number) && number > latest ? number : latest;
  }, 0);
}

function countSessionSamples(samples = [], sessionId = null) {
  if (!sessionId) return null;
  return (Array.isArray(samples) ? samples : []).filter((sample) => String(sample?.sessionId || "") === String(sessionId)).length;
}

export function getGpsSampleHealth({
  tracking,
  trackingSamples = [],
  now = Date.now(),
  staleThresholdMs = GPS_SAMPLE_LAG_WARNING_MS,
} = {}) {
  const latestGpsSampleAtMs = latestSampleTimestampMs(trackingSamples);
  const heartbeatAtMs = Number(tracking?.lastTelemetryAtMs || 0);
  const heartbeatAheadMs = heartbeatAtMs && latestGpsSampleAtMs ? heartbeatAtMs - latestGpsSampleAtMs : null;
  const telemetryStatus = (tracking?.telemetry?.status || "").toString().toLowerCase();
  const heartbeatActive = telemetryStatus === "active" || telemetryStatus === "ready" || telemetryStatus === "connected";
  const shouldWarn =
    !tracking?.isTrackingTerminal &&
    tracking?.connectionStatus !== "ended" &&
    heartbeatActive &&
    Number.isFinite(heartbeatAtMs) &&
    heartbeatAtMs > 0 &&
    (
      !latestGpsSampleAtMs ||
      (Number.isFinite(heartbeatAheadMs) && heartbeatAheadMs > staleThresholdMs)
    );

  return {
    latestGpsSampleAtMs: latestGpsSampleAtMs || null,
    latestGpsSampleAgeMs: latestGpsSampleAtMs ? Math.max(0, now - latestGpsSampleAtMs) : null,
    heartbeatAtMs: heartbeatAtMs || null,
    heartbeatAheadMs,
    heartbeatActive,
    shouldWarn,
  };
}

export function getTelemetryHealthSummary({
  tracking,
  trackingSamples = [],
  speedingRecords = [],
  offlineRecords = [],
  offlineAlertCount = null,
  now = Date.now(),
} = {}) {
  const gpsHealth = getGpsSampleHealth({ tracking, trackingSamples, now });
  const sessionId = tracking?.telemetry?.sessionId || null;
  const currentSessionSampleCount = countSessionSamples(trackingSamples, sessionId);
  const latestSpeedingAtMs = latestRecordTimestampMs(speedingRecords, ["eventAtMs", "endTimeMs", "startTimeMs", "createdAtMs"]);
  const latestOfflineAtMs = latestRecordTimestampMs(offlineRecords, ["checkpointTimeMs", "eventAtMs", "startTimeMs", "createdAtMs"]);
  const offlineCount = offlineAlertCount != null && Number.isFinite(Number(offlineAlertCount))
    ? Number(offlineAlertCount)
    : offlineRecords.length;

  return {
    gpsHealth,
    currentSessionSampleCount,
    latestSpeedingAtMs: latestSpeedingAtMs || null,
    latestSpeedingAgeMs: latestSpeedingAtMs ? Math.max(0, now - latestSpeedingAtMs) : null,
    latestOfflineAtMs: latestOfflineAtMs || null,
    latestOfflineAgeMs: latestOfflineAtMs ? Math.max(0, now - latestOfflineAtMs) : null,
    offlineCount,
    hasCurrentSessionWithoutPins:
      gpsHealth.heartbeatActive &&
      currentSessionSampleCount === 0 &&
      !tracking?.isTrackingTerminal &&
      tracking?.connectionStatus !== "ended",
  };
}

function toCoordinate(latitude, longitude) {
  const lat = Number(latitude);
  const lng = Number(longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function buildRouteBounds(points = []) {
  const validPoints = points.filter(Boolean);
  if (!validPoints.length) return null;
  const lats = validPoints.map((point) => point.lat);
  const lngs = validPoints.map((point) => point.lng);
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const latPadding = Math.min(
    MAX_REASONABLE_ROUTE_PADDING_DEGREES,
    Math.max(MIN_ROUTE_BOUND_PADDING_DEGREES, (maxLat - minLat) * 0.75)
  );
  const lngPadding = Math.min(
    MAX_REASONABLE_ROUTE_PADDING_DEGREES,
    Math.max(MIN_ROUTE_BOUND_PADDING_DEGREES, (maxLng - minLng) * 0.75)
  );

  return {
    minLat: minLat - latPadding,
    maxLat: maxLat + latPadding,
    minLng: minLng - lngPadding,
    maxLng: maxLng + lngPadding,
  };
}

function isCoordinateInsideBounds(point, bounds) {
  if (!point || !bounds) return Boolean(point);
  return (
    point.lat >= bounds.minLat &&
    point.lat <= bounds.maxLat &&
    point.lng >= bounds.minLng &&
    point.lng <= bounds.maxLng
  );
}

function filterSamplesToBounds(samples = [], bounds) {
  if (!bounds) return samples;
  return samples.filter((sample) => {
    const point = toCoordinate(
      sample?.snappedLatitude ?? sample?.rawLatitude ?? sample?.latitude ?? sample?.position?.lat,
      sample?.snappedLongitude ?? sample?.rawLongitude ?? sample?.longitude ?? sample?.position?.lng
    );
    return isCoordinateInsideBounds(point, bounds);
  });
}

export function getMapViewportPoints({
  viewMode,
  routePath = [],
  routeStartPosition = null,
  destinationDisplayPosition = null,
  routeSpeedingRecords = [],
  pickup = null,
  livePosition = null,
  dropoff = null,
} = {}) {
  const routePoints = routePath.filter(Boolean);
  if (viewMode === "route") {
    if (routePoints.length >= 2) {
      return [
        ...routePoints,
        ...routeSpeedingRecords.map((record) => record.position).filter(Boolean),
      ];
    }
    return [routeStartPosition, destinationDisplayPosition].filter(Boolean);
  }

  return routePoints.length ? routePoints : [pickup, livePosition, dropoff].filter(Boolean);
}

function offsetCoordinate(point, { eastMeters = 0, northMeters = 0 } = {}) {
  if (!point) return null;
  const latOffset = northMeters / 111320;
  const lngOffset = eastMeters / Math.max(1, 111320 * Math.cos((point.lat * Math.PI) / 180));
  return {
    lat: point.lat + latOffset,
    lng: point.lng + lngOffset,
  };
}

function isPointNearRecords(point, records = [], thresholdMeters = ROUTE_DOT_MATCH_METERS) {
  return records.some((record) => {
    const distance = distanceMeters(point, record?.position);
    return Number.isFinite(distance) && distance <= thresholdMeters;
  });
}

export function buildRouteVisualDots(segments = [], records = []) {
  const routeSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => Array.isArray(segment) && segment.length);
  const speedingRecords = records.filter((record) => record?.type === "speeding" && record?.position);
  const offlineRecords = records.filter((record) => record?.type === "offline" && record?.position);

  return routeSegments.flatMap((segment, segmentIndex) =>
    segment.map((point, pointIndex) => {
      const type = isPointNearRecords(point, offlineRecords)
        ? "offline"
        : isPointNearRecords(point, speedingRecords)
        ? "speeding"
        : "normal";

      return {
        key: `route-dot-${segmentIndex}-${pointIndex}`,
        position: point,
        type,
      };
    })
  );
}

export function buildRouteVisualConnectors(segments = [], records = [], options = {}) {
  const maxDistanceMeters = Number(options.maxDistanceMeters) || ROUTE_CONNECTOR_MAX_METERS;
  const routeSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => Array.isArray(segment) && segment.length >= 2);
  const speedingRecords = records.filter((record) => record?.type === "speeding" && record?.position);
  const offlineRecords = records.filter((record) => record?.type === "offline" && record?.position);

  return routeSegments.flatMap((segment, segmentIndex) => {
    const dots = segment.map((point) => {
      const type = isPointNearRecords(point, offlineRecords)
        ? "offline"
        : isPointNearRecords(point, speedingRecords)
        ? "speeding"
        : "normal";
      return { position: point, type };
    });

    return dots.slice(1).flatMap((dot, pointIndex) => {
      const previousDot = dots[pointIndex];
      if (!previousDot?.position || !dot?.position) return [];
      if (previousDot.type === "offline" || dot.type === "offline") return [];

      const distance = distanceMeters(previousDot.position, dot.position);
      if (!Number.isFinite(distance) || distance > maxDistanceMeters) return [];

      return [{
        key: `route-connector-${segmentIndex}-${pointIndex}`,
        path: [previousDot.position, dot.position],
        type: previousDot.type === "speeding" || dot.type === "speeding" ? "speeding" : "normal",
      }];
    });
  });
}

export function buildRouteVisualSegments(segments = []) {
  return (Array.isArray(segments) ? segments : [])
    .filter((segment) => Array.isArray(segment) && segment.length >= 2)
    .map((segment, segmentIndex) => ({
      key: `route-segment-${segmentIndex}`,
      path: segment,
      type: "normal",
    }));
}

export function buildRouteVisualGapConnectors(segments = [], options = {}) {
  const maxDistanceMeters = Number(options.maxDistanceMeters) || ROUTE_GAP_CONNECTOR_MAX_METERS;
  const routeSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => Array.isArray(segment) && segment.length >= 2);

  return routeSegments.slice(1).flatMap((segment, segmentIndex) => {
    const previousSegment = routeSegments[segmentIndex];
    const previousPoint = previousSegment?.at(-1);
    const nextPoint = segment?.[0];
    if (!previousPoint || !nextPoint) return [];

    const distance = distanceMeters(previousPoint, nextPoint);
    if (!Number.isFinite(distance) || distance > maxDistanceMeters) return [];

    return [{
      key: `route-gap-connector-${segmentIndex}`,
      path: [previousPoint, nextPoint],
      type: "estimated",
    }];
  });
}

export function buildRouteGapRequests(segments = [], options = {}) {
  const minDistanceMeters = Number(options.minDistanceMeters) || ROUTED_GAP_MIN_METERS;
  const maxDistanceMeters = Number(options.maxDistanceMeters) || ROUTED_GAP_MAX_METERS;
  const routeSegments = (Array.isArray(segments) ? segments : [])
    .filter((segment) => Array.isArray(segment) && segment.length >= 2);

  return routeSegments.slice(1).flatMap((segment, segmentIndex) => {
    const previousSegment = routeSegments[segmentIndex];
    const origin = previousSegment?.at(-1);
    const destination = segment?.[0];
    if (!origin || !destination) return [];

    const distance = distanceMeters(origin, destination);
    if (!Number.isFinite(distance) || distance < minDistanceMeters || distance > maxDistanceMeters) {
      return [];
    }

    return [{
      key: `route-road-gap-${segmentIndex}`,
      origin,
      destination,
      distanceMeters: distance,
    }];
  });
}

export function buildRouteGapFallbackConnectors(gapRequests = []) {
  return (Array.isArray(gapRequests) ? gapRequests : []).flatMap((request) => {
    if (!request?.origin || !request?.destination) return [];
    return [{
      key: request.key,
      path: [request.origin, request.destination],
      type: "offline",
      fallback: true,
    }];
  });
}

async function requestOfflineRoadRoute(request) {
  try {
    const response = await httpClient.post(Endpoints.SNAP_ROUTE, {
      points: [request.origin, request.destination],
      preferRoutedPath: true,
    });
    const path = Array.isArray(response?.data?.data) ? response.data.data : [];
    const normalizedPath = path
      .map((point) => ({
        lat: Number(point?.lat),
        lng: Number(point?.lng),
      }))
      .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng));

    if (normalizedPath.length < 2) return null;
    return {
      key: request.key,
      path: normalizedPath,
      type: "offline",
    };
  } catch (error) {
    console.warn("[Route Taken] Offline road route lookup failed", {
      message: error?.response?.data?.message || error?.message || String(error),
      origin: request.origin,
      destination: request.destination,
    });
    return null;
  }
}


export function buildRouteDisplaySegments(samples = [], options = {}) {
  const {
    splitLongDisplayJoins = false,
    ...cleanOptions
  } = options;
  const cleanSegments = buildCleanRouteSegments(samples, {
    ...cleanOptions,
    maxJumpMeters: Math.min(Number(cleanOptions.maxJumpMeters) || 1200, 1200),
    maxJumpWithoutTimeMeters: Math.min(Number(cleanOptions.maxJumpWithoutTimeMeters) || 500, 500),
    maxDrawSegmentMeters: splitLongDisplayJoins
      ? Number(cleanOptions.maxDrawSegmentMeters) || 220
      : null,
    maxSnappedDrawSegmentMeters: splitLongDisplayJoins
      ? Number(cleanOptions.maxSnappedDrawSegmentMeters) || 350
      : null,
  });
  return cleanSegments.flatMap((segment) => splitRouteAtSelfIntersections(segment));
}

export function buildSavedRouteDisplaySegments(savedRoutePath = []) {
  const cleanSegments = buildCleanRouteSegments(savedRoutePath, {
    minPointDistanceMeters: 35,
    simplifyToleranceMeters: 10,
    maxJumpMeters: SAVED_ROUTE_MAX_JOIN_METERS,
    maxJumpWithoutTimeMeters: SAVED_ROUTE_MAX_JOIN_METERS,
    maxRoutePoints: 700,
    requireReliableOrder: false,
  });
  return cleanSegments.flatMap((segment) => splitRouteAtSelfIntersections(segment));
}

export function resolveCompletedRouteSegments({
  isTerminalTrip = false,
  savedRoutePath = [],
  routeSampleSegments = [],
  maxSavedJoinMeters = SAVED_ROUTE_SAMPLE_FALLBACK_JOIN_METERS,
} = {}) {
  const hasSampleRoute = routeSampleSegments.some((segment) => segment.length >= 2);
  if (isTerminalTrip && savedRoutePath.length >= 2) {
    const savedSegments = buildSavedRouteDisplaySegments(savedRoutePath);
    const hasSavedRoute = savedSegments.some((segment) => segment.length >= 2);
    const savedRouteHasLongJoin = savedSegments.some((segment) =>
      hasRouteJoinLongerThan(segment, maxSavedJoinMeters)
    );

    if (hasSavedRoute && (!savedRouteHasLongJoin || !hasSampleRoute)) {
      return savedSegments;
    }
  }

  return hasSampleRoute ? routeSampleSegments : [];
}

export function getTrackingMapNotice({
  trip,
  hasLiveTelemetry,
  hasRecordedTracking,
  isPreInspection,
  isTerminalTrip,
  routeEndPosition = null,
  destinationPosition = null,
  destinationMatchMeters = TRACKING_DESTINATION_MATCH_METERS,
} = {}) {
  if (!trip || hasLiveTelemetry) return null;
  if (isTerminalTrip && !hasRecordedTracking) {
    return "No driver tracking session was recorded for this completed trip. Showing planned pickup and drop-off only.";
  }
  if (isTerminalTrip && hasRecordedTracking) {
    const distanceFromDestination = distanceMeters(routeEndPosition, destinationPosition);
    if (Number.isFinite(distanceFromDestination) && distanceFromDestination > destinationMatchMeters) {
      return "Tracking stopped before the planned destination. Showing the recorded route up to the last uploaded point.";
    }
    return null;
  }
  if (isPreInspection) {
    return "Driver is completing pre-inspection. Showing planned pickup and drop-off until live tracking starts.";
  }
  return "Waiting for the driver to connect. Showing planned pickup and drop-off.";
}

function svgMarker(fill, text, { stroke = "#ffffff", textColor = "#ffffff", pulse = null } = {}) {
  const pulseMarkup = pulse
    ? `<circle cx="32" cy="18" r="13" fill="${pulse}" opacity="0.35"><animate attributeName="r" values="13;20;13" dur="1.8s" repeatCount="indefinite" /><animate attributeName="opacity" values="0.35;0;0.35" dur="1.8s" repeatCount="indefinite" /></circle>`
    : "";
  const textMarkup = text
    ? `<text x="32" y="22" text-anchor="middle" font-family="Arial, sans-serif" font-size="14" font-weight="700" fill="${textColor}">${text}</text>`
    : `<circle cx="32" cy="18" r="6" fill="${textColor}" />`;
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
      ${pulseMarkup}
      <path d="M32 6C23.163 6 16 13.163 16 22c0 11.7 13.563 25.646 15.111 27.211a1.25 1.25 0 0 0 1.778 0C34.437 47.646 48 33.7 48 22 48 13.163 40.837 6 32 6Z" fill="${fill}" stroke="${stroke}" stroke-width="2.5"/>
      ${textMarkup}
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

function svgRouteDot(fill, { stroke = "#ffffff", opacity = 1 } = {}) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18">
      <circle cx="9" cy="9" r="7" fill="${fill}" fill-opacity="${opacity}" stroke="${stroke}" stroke-opacity="${opacity}" stroke-width="3"/>
    </svg>
  `;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}

const liveIcon = svgMarker("#e57a2f", "", { textColor: "#fff7ef", pulse: "#f59e0b" });
const speedingIcon = svgMarker("#9f2a1e", "", { textColor: "#fff4f0" });
const offlineIcon = svgMarker("#6b53d5", "", { textColor: "#f5f3ff" });
const startIcon = svgMarker("#1d73e8", "S", { textColor: "#ffffff" });
const destinationIcon = svgMarker("#16834a", "D", { textColor: "#ffffff" });
const routeDotIcons = {
  normal: svgRouteDot(ROUTE_NORMAL_COLOR, { opacity: 0.5 }),
  speeding: svgRouteDot(ROUTE_SPEEDING_COLOR, { opacity: 0.5 }),
  offline: svgRouteDot(ROUTE_OFFLINE_COLOR, { opacity: 0.5 }),
};

const InfoCard = ({ label, value, helper, dark = false }) => <div className={`rounded-2xl border px-4 py-4 shadow-[0_10px_28px_rgba(15,23,42,0.04)] ${dark ? "border-[#0f2017]/10 bg-[#101915] text-white" : "border-[#ece5dd] bg-white"}`}>
  <div className={`text-[11px] font-semibold uppercase tracking-[0.18em] ${dark ? "text-[#7be0b0]" : "text-[#9a8473]"}`}>{label}</div>
  <div className={`mt-2 text-[1.25rem] font-semibold leading-tight ${dark ? "text-white" : "text-[#1f1712]"}`}>{value}</div>
  {helper ? <div className={`mt-2 text-xs leading-5 ${dark ? "text-[#c2dfcf]" : "text-[#7a695b]"}`}>{helper}</div> : null}
</div>;

const healthChipClasses = {
  good: "border-[#cfead7] bg-[#f3fbf5] text-[#1f6b3d]",
  warn: "border-[#f2dfaa] bg-[#fff8e8] text-[#8a610c]",
  bad: "border-[#f8cbd3] bg-[#fff0f3] text-[#b83a56]",
  neutral: "border-[#ece5dd] bg-white text-[#4f4037]",
};

const TelemetryHealthChip = ({ label, value, helper, tone = "neutral" }) => (
  <div className={`min-w-0 rounded-xl border px-3 py-2 shadow-[0_8px_22px_rgba(15,23,42,0.035)] ${healthChipClasses[tone] || healthChipClasses.neutral}`}>
    <div className="truncate text-[10px] font-semibold uppercase tracking-[0.14em] opacity-75">{label}</div>
    <div className="mt-1 truncate text-sm font-semibold leading-tight">{value}</div>
    {helper ? <div className="mt-1 truncate text-[11px] leading-4 opacity-75">{helper}</div> : null}
  </div>
);

const formatRoadLimit = (tracking) => {
  const number = Number(tracking?.displaySpeedLimitKph ?? tracking?.speedLimitKph);
  if (Number.isFinite(number) && number > 0) {
    return `${formatSpeed(number)}${tracking?.speedLimitEstimated ? " estimated" : ""}`;
  }
  if (tracking?.providerStatus && tracking.providerStatus !== "ready") {
    return tracking.providerStatus.replace(/_/g, " ");
  }
  return tracking?.speedLimitMessage || tracking?.providerMessage || "Road limit lookup pending";
};

export const LiveTrackingMap = ({
  trip,
  tracking,
  infringements = [],
  trackingEvents = [],
  trackingSamples = [],
  offlineAlertCount = null,
  height = 420,
}) => {
  const mapRef = useRef(null);
  const terminalRouteTripIdRef = useRef(null);
  const initialViewportTripIdRef = useRef(null);
  const routeFitPendingRef = useRef(false);
  const routePathFitTripIdRef = useRef(null);
  const autoFollowVehicleRef = useRef(false);
  const pickup = useMemo(
    () => toCoordinate(trip?.pickUpLatitude, trip?.pickUpLongitude),
    [trip?.pickUpLatitude, trip?.pickUpLongitude]
  );
  const dropoff = useMemo(
    () => toCoordinate(trip?.dropOffLatitude, trip?.dropOffLongitude),
    [trip?.dropOffLatitude, trip?.dropOffLongitude]
  );
  const reportedLivePosition = tracking?.position || null;
  const plannedRoutePath = useMemo(
    () => [pickup, dropoff].filter(Boolean),
    [dropoff, pickup]
  );
  const routeBounds = useMemo(
    () => buildRouteBounds(plannedRoutePath),
    [plannedRoutePath]
  );
  const livePosition = reportedLivePosition;
  const boundedTrackingSamples = useMemo(
    () => filterSamplesToBounds(trackingSamples, routeBounds),
    [routeBounds, trackingSamples]
  );
  const boundedInfringements = useMemo(
    () => infringements.filter((record) => !record?.position || isCoordinateInsideBounds(record.position, routeBounds)),
    [infringements, routeBounds]
  );
  const tripStatus = Number(trip?.status);
  const isTerminalTrip = tripStatus === TripStatus.Completed || tripStatus === TripStatus.Cancelled;
  const [viewMode, setViewMode] = useState(isTerminalTrip ? "route" : "track");
  const routeSpeedingRecords = useMemo(
    () => infringements.filter((record) => record?.type === "speeding" && record?.position),
    [infringements]
  );
  const speedingRecords = useMemo(
    () => (viewMode === "route" ? routeSpeedingRecords : boundedInfringements)
      .filter((record) => record?.type === "speeding"),
    [boundedInfringements, routeSpeedingRecords, viewMode]
  );
  const offlineRecords = useMemo(
    () => boundedInfringements.filter((record) => record?.type === "offline"),
    [boundedInfringements]
  );
  const [zoomLevel, setZoomLevel] = useState(livePosition ? LIVE_TRACKING_ZOOM : CONTEXT_ZOOM);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [routedGapConnectors, setRoutedGapConnectors] = useState([]);
  const savedRoutePath = useMemo(() => {
    const routePath = tracking?.routeSnapshot?.routePath || trip?.driverRouteSnapshot?.routePath;
    return Array.isArray(routePath)
      ? routePath
          .map((point) => toCoordinate(point?.lat, point?.lng))
          .filter((point) => isCoordinateInsideBounds(point, routeBounds))
      : [];
  }, [routeBounds, tracking?.routeSnapshot?.routePath, trip?.driverRouteSnapshot?.routePath]);
  const routeSampleSegments = useMemo(() => {
    if (viewMode !== "route") return [];
    return buildRouteDisplaySegments(boundedTrackingSamples, {
      minPointDistanceMeters: 6,
      maxJumpMeters: 1200,
      maxJumpWithoutTimeMeters: 500,
      maxRoutePoints: 700,
      simplifyToleranceMeters: 5,
      includeLastPoint: true,
    });
  }, [boundedTrackingSamples, viewMode]);
  const routeSegments = useMemo(() => {
    if (viewMode === "route") {
      return resolveCompletedRouteSegments({
        isTerminalTrip,
        savedRoutePath,
        routeSampleSegments,
      });
    }

    const samples = viewMode === "route" ? [...trackingSamples] : [...boundedTrackingSamples];
    if (viewMode === "track" && livePosition) {
      const lastSample = buildCleanRoutePath(samples).at(-1);
      const distanceFromLast = lastSample ? distanceMeters(lastSample, livePosition) : null;
      if (!Number.isFinite(distanceFromLast) || distanceFromLast < 250) {
        samples.push({
          position: livePosition,
          latitude: livePosition.lat,
          longitude: livePosition.lng,
          recordedAtMs: tracking?.lastTelemetryAtMs || Date.now(),
        });
      }
    }

    const liveSampleSegments = viewMode === "route"
      ? buildSolidRoadRouteSegments(samples, {
          maxJumpMeters: 1200,
          maxJumpWithoutTimeMeters: 500,
          includeLastPoint: false,
        })
      : buildCleanRouteSegments(samples);
    const cleanLiveSegments = liveSampleSegments;
    if (cleanLiveSegments.some((segment) => segment.length >= 2)) {
      return cleanLiveSegments;
    }

    if (isTerminalTrip && savedRoutePath.length >= 2) {
      return buildCleanRouteSegments(savedRoutePath, {
        minPointDistanceMeters: 35,
        simplifyToleranceMeters: 10,
        maxRoutePoints: 350,
        requireReliableOrder: false,
      });
    }

    return cleanLiveSegments;
  }, [boundedTrackingSamples, isTerminalTrip, livePosition, routeSampleSegments, savedRoutePath, tracking?.lastTelemetryAtMs, trackingSamples, viewMode]);
  const displayedRouteSegments = routeSegments;
  const mapInfringementRecords = viewMode === "route" ? routeSpeedingRecords : boundedInfringements;
  const routeVisualRecords = useMemo(
    () => viewMode === "route"
      ? [...routeSpeedingRecords, ...offlineRecords]
      : boundedInfringements,
    [boundedInfringements, offlineRecords, routeSpeedingRecords, viewMode]
  );
  const routeVisualDots = useMemo(
    () => buildRouteVisualDots(displayedRouteSegments, routeVisualRecords),
    [displayedRouteSegments, routeVisualRecords]
  );
  const showRouteDebugDots = useMemo(
    () => typeof window === "undefined" || new URLSearchParams(window.location.search).get("routeDebugDots") !== "0",
    []
  );
  const routeVisualConnectors = useMemo(
    () => buildRouteVisualConnectors(displayedRouteSegments, routeVisualRecords),
    [displayedRouteSegments, routeVisualRecords]
  );
  const routeVisualSpeedingConnectors = useMemo(
    () => routeVisualConnectors.filter((connector) => connector.type === "speeding"),
    [routeVisualConnectors]
  );
  const routeVisualSegments = useMemo(
    () => buildRouteVisualSegments(displayedRouteSegments),
    [displayedRouteSegments]
  );
  const routeVisualGapConnectors = useMemo(
    () => buildRouteVisualGapConnectors(displayedRouteSegments),
    [displayedRouteSegments]
  );
  const routeGapRequests = useMemo(
    () => buildRouteGapRequests(displayedRouteSegments),
    [displayedRouteSegments]
  );
  const routeGapFallbackConnectors = useMemo(
    () => buildRouteGapFallbackConnectors(routeGapRequests),
    [routeGapRequests]
  );
  const offlineGapConnectors = useMemo(() => {
    const routedByKey = new Map(routedGapConnectors.map((connector) => [connector.key, connector]));
    return routeGapFallbackConnectors.map((connector) => routedByKey.get(connector.key) || connector);
  }, [routeGapFallbackConnectors, routedGapConnectors]);
  const routePath = useMemo(() => displayedRouteSegments.flat(), [displayedRouteSegments]);
  const routeEndPosition = routePath.at(-1) || null;
  const routeStartPosition = pickup || (viewMode === "route" ? routePath[0] : null);
  const routeDestinationPosition = dropoff || (viewMode === "route" ? routePath.at(-1) : null);
  const destinationDisplayPosition = useMemo(() => {
    if (!routeStartPosition || !routeDestinationPosition) return routeDestinationPosition;
    const markerDistance = distanceMeters(routeStartPosition, routeDestinationPosition);
    if (Number.isFinite(markerDistance) && markerDistance < 25) {
      return offsetCoordinate(routeDestinationPosition, { eastMeters: 28, northMeters: 18 });
    }
    return routeDestinationPosition;
  }, [routeDestinationPosition, routeStartPosition]);
  const center = useMemo(
    () => livePosition || pickup || dropoff || defaultCenter,
    [dropoff, livePosition, pickup]
  );
  const controlledCenter = !mapLoaded ? center : undefined;
  const telemetryPointCount = boundedTrackingSamples.length || trackingEvents.length;
  const telemetryHealth = useMemo(
    () => getTelemetryHealthSummary({
      tracking,
      trackingSamples,
      speedingRecords: routeSpeedingRecords,
      offlineRecords,
      offlineAlertCount,
    }),
    [offlineAlertCount, offlineRecords, routeSpeedingRecords, tracking, trackingSamples]
  );
  const gpsSampleHealth = telemetryHealth.gpsHealth;
  const routePointCount = routePath.length;
  const hasLiveTelemetry = Boolean(livePosition);
  const hasRecordedTracking = Boolean(
    telemetryPointCount ||
    savedRoutePath.length >= 2 ||
    tracking?.telemetry?.sessionId ||
    tracking?.lastTelemetryAtMs
  );
  const shouldShowPlannedRoute =
    !hasLiveTelemetry && routePointCount < 2 && plannedRoutePath.length === 2;
  const isPreInspection = tripStatus === 6;
  const mapNotice = getTrackingMapNotice({
    trip,
    hasLiveTelemetry,
    hasRecordedTracking,
    isPreInspection,
    isTerminalTrip,
    routeEndPosition,
    destinationPosition: dropoff,
  });

  const focusMap = useCallback((target, zoom = LIVE_TRACKING_ZOOM) => {
    const map = mapRef.current;
    if (!map || !target) return;
    map.panTo(target);
    map.setZoom(zoom);
    setZoomLevel(zoom);
  }, []);

  const fitRouteBounds = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const points = getMapViewportPoints({
      viewMode,
      routePath,
      routeStartPosition,
      destinationDisplayPosition,
      routeSpeedingRecords,
      pickup,
      livePosition,
      dropoff,
    });
    if (points.length === 0) {
      focusMap(center, CONTEXT_ZOOM);
      return;
    }
    if (points.length === 1) {
      focusMap(points[0], CONTEXT_ZOOM);
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 80);
    const nextZoom = Math.min(map.getZoom() || CONTEXT_ZOOM, 16);
    map.setZoom(nextZoom);
    setZoomLevel(nextZoom);
  }, [center, destinationDisplayPosition, dropoff, focusMap, livePosition, pickup, routePath, routeSpeedingRecords, routeStartPosition, viewMode]);

  useEffect(() => {
    if (!isTerminalTrip || !trip?.id || terminalRouteTripIdRef.current === trip.id) return;
    terminalRouteTripIdRef.current = trip.id;
    routeFitPendingRef.current = true;
    setViewMode("route");
  }, [isTerminalTrip, trip?.id]);

  useEffect(() => {
    if (!mapLoaded || !trip?.id) return;
    const viewportKey = `${trip.id}:${viewMode}`;
    if (initialViewportTripIdRef.current === viewportKey) return;
    initialViewportTripIdRef.current = viewportKey;

    if (viewMode === "track") {
      if (!livePosition) {
        fitRouteBounds();
        return;
      }
      focusMap(livePosition, LIVE_TRACKING_ZOOM);
      return;
    }

    if (viewMode === "route") {
      routeFitPendingRef.current = true;
    }
  }, [fitRouteBounds, focusMap, livePosition, mapLoaded, trip?.id, viewMode]);

  useEffect(() => {
    if (!mapLoaded || viewMode !== "route" || !routeFitPendingRef.current) return;
    if (routePointCount >= 2 || routeStartPosition || destinationDisplayPosition) {
      routeFitPendingRef.current = false;
      fitRouteBounds();
    }
  }, [destinationDisplayPosition, fitRouteBounds, mapLoaded, routePointCount, routeStartPosition, viewMode]);

  useEffect(() => {
    if (!mapLoaded || viewMode !== "route" || routePointCount < 2 || !trip?.id) return;
    const viewportKey = `${trip.id}:route-path`;
    if (routePathFitTripIdRef.current === viewportKey) return;
    routePathFitTripIdRef.current = viewportKey;
    routeFitPendingRef.current = false;
    fitRouteBounds();
  }, [fitRouteBounds, mapLoaded, routePointCount, trip?.id, viewMode]);

  useEffect(() => {
    if (!mapLoaded || viewMode !== "track" || !autoFollowVehicleRef.current || !livePosition) return;
    const map = mapRef.current;
    if (!map) return;
    map.panTo(livePosition);
  }, [livePosition, mapLoaded, viewMode]);

  useEffect(() => {
    if (!mapLoaded || viewMode !== "route" || routeGapRequests.length === 0) {
      setRoutedGapConnectors([]);
      return undefined;
    }

    let cancelled = false;

    Promise.all(routeGapRequests.map((request) =>
      requestOfflineRoadRoute(request)
    )).then((connectors) => {
      if (cancelled) return;
      setRoutedGapConnectors(connectors.filter((connector) => connector?.path?.length >= 2));
    });

    return () => {
      cancelled = true;
    };
  }, [mapLoaded, routeGapRequests, viewMode]);

  const handleMapLoad = (map) => {
    mapRef.current = map;
    setMapLoaded(true);
  };

  const handleManualZoom = (delta) => {
    const map = mapRef.current;
    if (!map) return;
    const nextZoom = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, (map.getZoom() || zoomLevel || CONTEXT_ZOOM) + delta));
    map.setZoom(nextZoom);
    setZoomLevel(nextZoom);
  };

  const handleTrackVehicle = () => {
    autoFollowVehicleRef.current = true;
    setViewMode("track");
    if (!livePosition) {
      fitRouteBounds();
      return;
    }
    focusMap(livePosition, LIVE_TRACKING_ZOOM);
  };

  const handleRouteView = () => {
    autoFollowVehicleRef.current = false;
    routeFitPendingRef.current = true;
    setViewMode("route");
  };

  if (!trip && !livePosition) {
    return <div className="rounded-[28px] border border-[#ece5dd] bg-white p-6"><Empty description="Select a trip to load the live tracking map." /></div>;
  }

  const isEnded = tracking?.connectionStatus === "ended" || tracking?.isTrackingTerminal;
  const isLive = tracking?.connectionStatus === "live";
  const isStale = tracking?.connectionStatus === "stale";
  const isPaused = tracking?.trackingStatusKey === "tracking_paused";
  const pingLabel = isLive
    ? "Ping online"
    : isStale
    ? "Ping delayed"
    : isPaused
    ? "No server upload"
    : isEnded
    ? "Ping stopped"
    : "Ping offline";
  const trackingLabel = isLive
    ? "Tracking Active"
    : isStale
    ? "Tracking Stale"
    : isPaused
    ? "Tracking Paused"
    : isEnded
    ? "Tracking Ended"
    : "Tracking Offline";
  const pingPillClass = isLive
    ? "border-[#cfead7] bg-[#edf8ef] text-[#3e7a50]"
    : isStale
    ? "border-[#f2dfaa] bg-[#fff6dc] text-[#9a6c08]"
    : isEnded || isPaused
    ? "border-[#ded6cd] bg-[#f7f3ef] text-[#7a695b]"
    : "border-[#f8cbd3] bg-[#fff0f3] text-[#c43f5b]";
  const pingDotClass = isLive ? "bg-[#31b879]" : isStale ? "bg-[#d9a520]" : isEnded || isPaused ? "bg-[#8c7b6b]" : "bg-[#ef3f68]";
  const heartbeatTone = isLive ? "good" : isStale ? "warn" : isEnded || isPaused ? "neutral" : "bad";
  const gpsTone = gpsSampleHealth.shouldWarn ? "warn" : gpsSampleHealth.latestGpsSampleAtMs ? "good" : "neutral";
  const currentSessionTone = telemetryHealth.hasCurrentSessionWithoutPins
    ? "warn"
    : telemetryHealth.currentSessionSampleCount > 0
    ? "good"
    : "neutral";
  const latestSpeedingHelper = telemetryHealth.latestSpeedingAgeMs != null
    ? `${formatHeartbeatAge(telemetryHealth.latestSpeedingAgeMs)} ago`
    : "No speeding pins";
  const latestOfflineHelper = telemetryHealth.latestOfflineAgeMs != null
    ? `${formatHeartbeatAge(telemetryHealth.latestOfflineAgeMs)} ago`
    : "No offline alert";

  return <div className="overflow-hidden rounded-[28px] border border-[#ece5dd] bg-[#fffdfa] shadow-[0_18px_55px_rgba(15,23,42,0.05)]">
    <div className="grid grid-cols-1 gap-0 xl:grid-cols-[320px,1fr]">
      <aside className="border-b border-[#f2e8de] bg-[linear-gradient(180deg,#fffaf4_0%,#fff 100%)] p-5 xl:border-b-0 xl:border-r">
        <div className="flex flex-col gap-3 text-[11px] font-semibold uppercase tracking-[0.18em]">
          <span className="inline-flex w-fit items-center rounded-full bg-[#fff1e6] px-4 py-2 text-[#d0672d]">
            Live Firestore listener
          </span>
          <span className={`inline-flex w-fit items-center rounded-full px-4 py-2 ${isLive ? "bg-[#f0ffe9] text-[#5e9f3d]" : isStale ? "bg-[#fff6dc] text-[#b6861b]" : isEnded || isPaused ? "bg-[#f7f3ef] text-[#7a695b]" : "bg-[#ffefef] text-[#ca5a5a]"}`}>
            {trackingLabel}
          </span>
          <div className={`inline-flex w-fit items-center gap-3 rounded-full border px-4 py-2 ${pingPillClass}`}>
            <span className={`h-3 w-3 rounded-full ${pingDotClass}`} />
            {pingLabel}
          </div>
        </div>

        <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-1">
          <InfoCard
            label="Live Speed"
            value={formatSpeed(tracking?.speedKph)}
            helper={formatRoadLimit(tracking)}
            dark
          />
          <InfoCard
            label="Current Road"
            value={tracking?.roadName || "Road lookup pending"}
          />
          <InfoCard
            label="Last Ping Age"
            value={formatHeartbeatState(tracking)}
          />
          <InfoCard
            label="Last GPS Pin"
            value={
              gpsSampleHealth.latestGpsSampleAgeMs != null
                ? formatHeartbeatAge(gpsSampleHealth.latestGpsSampleAgeMs)
                : "No GPS pins yet"
            }
            helper={
              gpsSampleHealth.latestGpsSampleAtMs
                ? "Since last route sample"
                : "Waiting for route samples"
            }
          />
          {gpsSampleHealth.shouldWarn ? <div className="rounded-2xl border border-[#f2c588] bg-[#fff8e8] px-4 py-4 text-sm leading-6 text-[#6f4a13] shadow-[0_10px_28px_rgba(15,23,42,0.04)]">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#b46a12]">GPS samples delayed</div>
            <div className="mt-2 font-semibold text-[#4b3410]">Heartbeat active, waiting for GPS route pins</div>
            <div className="mt-1 text-xs text-[#7a5a22]">
              The driver app is sending keepalive updates, but no newer route samples have arrived yet.
            </div>
          </div> : null}
          <InfoCard
            label="Server Pings"
            value={telemetryPointCount}
            helper={
              isPaused
                ? "Driver app paused before a backend heartbeat was saved"
                : isEnded
                ? "Stopped when trip was completed"
                : `${formatHeartbeatAge(tracking?.heartbeatAgeMs)} since last heartbeat`
            }
          />
          <InfoCard
            label="Connection State"
            value={<ConnectionStatusBadge status={tracking?.connectionStatus || "offline"} />}
          />
          <InfoCard
            label="Offline Alerts"
            value={offlineAlertCount ?? offlineRecords.length}
          />
        </div>
      </aside>

      <section className="min-w-0">
        <div className="border-b border-[#f2e8de] bg-white px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-[2rem] font-semibold leading-none text-[#1d1814]">Google Map Tracking</div>
              <div className="text-base leading-7 text-[#6f6258]">
                Viewing live driver position for {trip?.id ? `MYC ref #${trip.id}` : "the selected trip"}.
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={handleTrackVehicle}
                className={`rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition ${
                  viewMode === "track"
                    ? "border border-[#e57a2f] bg-[#e57a2f] text-white"
                    : "border border-[#f1dcc9] bg-white text-[#7a695b] hover:bg-[#fff7ef]"
                }`}
              >
                Track Vehicle
              </button>
              <button
                type="button"
                onClick={handleRouteView}
                className={`rounded-full px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] transition ${
                  viewMode === "route"
                    ? "border border-[#1d73e8] bg-[#1d73e8] text-white"
                    : "border border-[#f1dcc9] bg-white text-[#7a695b] hover:bg-[#f4f8ff]"
                }`}
              >
                Route Taken
              </button>
            </div>
          </div>
        </div>
        <div className="border-b border-[#f2e8de] bg-[#fffdf9] px-5 py-3">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <TelemetryHealthChip
              label="Heartbeat"
              value={pingLabel}
              helper={formatHeartbeatState(tracking)}
              tone={heartbeatTone}
            />
            <TelemetryHealthChip
              label="GPS pins"
              value={
                gpsSampleHealth.latestGpsSampleAgeMs != null
                  ? formatHeartbeatAge(gpsSampleHealth.latestGpsSampleAgeMs)
                  : "Waiting"
              }
              helper={gpsSampleHealth.shouldWarn ? "Keepalive without route pins" : "Last route sample"}
              tone={gpsTone}
            />
            <TelemetryHealthChip
              label="Session pins"
              value={
                telemetryHealth.currentSessionSampleCount == null
                  ? "No session"
                  : `${telemetryHealth.currentSessionSampleCount} pins`
              }
              helper={telemetryHealth.hasCurrentSessionWithoutPins ? "Current session has no pins" : "Current GPS session"}
              tone={currentSessionTone}
            />
            <TelemetryHealthChip
              label="Speeding"
              value={`${routeSpeedingRecords.length} pins`}
              helper={latestSpeedingHelper}
              tone={routeSpeedingRecords.length ? "warn" : "neutral"}
            />
            <TelemetryHealthChip
              label="Offline"
              value={`${telemetryHealth.offlineCount} alerts`}
              helper={latestOfflineHelper}
              tone={telemetryHealth.offlineCount ? "warn" : "neutral"}
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 border-b border-[#f2e8de] bg-[#fffaf4] px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6f6258]">
          <span className="text-[#9a8473]">Route legend</span>
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-[0_6px_18px_rgba(15,23,42,0.04)]">
            <span className="h-3 w-3 rounded-full border-2 border-white shadow-[0_1px_4px_rgba(15,23,42,0.18)]" style={{ backgroundColor: ROUTE_NORMAL_COLOR }} />
            Connected
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-[0_6px_18px_rgba(15,23,42,0.04)]">
            <span className="h-3 w-3 rounded-full border-2 border-white shadow-[0_1px_4px_rgba(15,23,42,0.18)]" style={{ backgroundColor: ROUTE_OFFLINE_COLOR }} />
            Offline / Lost
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-2 shadow-[0_6px_18px_rgba(15,23,42,0.04)]">
            <span className="h-3 w-3 rounded-full border-2 border-white shadow-[0_1px_4px_rgba(15,23,42,0.18)]" style={{ backgroundColor: ROUTE_SPEEDING_COLOR }} />
            Speeding
          </span>
        </div>
    <div className="relative">
      <div className="absolute right-4 top-4 z-10 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => handleManualZoom(1)}
          className="rounded-xl border border-[#efd7c2] bg-white px-4 py-2 text-sm font-semibold text-[#3f332b] shadow-[0_10px_25px_rgba(15,23,42,0.08)] transition hover:bg-[#fff7ef]"
        >
          Zoom In
        </button>
        <button
          type="button"
          onClick={() => handleManualZoom(-1)}
          className="rounded-xl border border-[#efd7c2] bg-white px-4 py-2 text-sm font-semibold text-[#3f332b] shadow-[0_10px_25px_rgba(15,23,42,0.08)] transition hover:bg-[#fff7ef]"
        >
          Zoom Out
        </button>
        <button
          type="button"
          onClick={handleTrackVehicle}
          className="rounded-xl border border-[#e57a2f] bg-[#e57a2f] px-4 py-2 text-sm font-semibold text-white shadow-[0_10px_25px_rgba(15,23,42,0.08)] transition hover:bg-[#cf6922]"
        >
          Locate Vehicle
        </button>
      </div>
      <div className="absolute right-4 bottom-16 z-10 flex flex-col items-end gap-3">
        <div className="rounded-full border border-white/60 bg-white/95 px-4 py-3 text-sm text-[#59493f] shadow-[0_12px_30px_rgba(15,23,42,0.08)]">
          {speedingRecords.length} infringement pins
        </div>
      </div>
      <GoogleMap
        center={controlledCenter}
        zoom={zoomLevel || (viewMode === "track" && livePosition ? LIVE_TRACKING_ZOOM : CONTEXT_ZOOM)}
        onLoad={handleMapLoad}
        onZoomChanged={() => {
          const map = mapRef.current;
          if (!map) return;
          setZoomLevel(map.getZoom() || CONTEXT_ZOOM);
        }}
        mapContainerStyle={{ width: "100%", height }}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: false,
        }}
      >
        {shouldShowPlannedRoute ? <>
          <Polyline
            path={plannedRoutePath}
            options={{
              strokeColor: "#ffffff",
              strokeOpacity: 0.9,
              strokeWeight: 8,
              clickable: false,
              zIndex: 1,
            }}
          />
          <Polyline
            path={plannedRoutePath}
            options={{
              strokeColor: "#1d73e8",
              strokeOpacity: 0.85,
              strokeWeight: 4,
              clickable: false,
              zIndex: 2,
              geodesic: true,
            }}
          />
        </> : null}
        {routeStartPosition ? <Marker position={routeStartPosition} icon={{ url: startIcon }} title={viewMode === "route" ? "Route start" : "Start location"} zIndex={20} /> : null}
        {destinationDisplayPosition ? <Marker position={destinationDisplayPosition} icon={{ url: destinationIcon }} title={viewMode === "route" ? "Route destination" : "Destination location"} zIndex={21} /> : null}
        {viewMode === "route" && routeVisualGapConnectors.length ? routeVisualGapConnectors.map((connector) => <Polyline
            key={connector.key}
            path={connector.path}
            options={{
              strokeColor: ROUTE_NORMAL_COLOR,
              strokeOpacity: 0.3,
              strokeWeight: 5,
              clickable: false,
              zIndex: 4,
            }}
          />) : null}
        {viewMode === "route" && offlineGapConnectors.length ? offlineGapConnectors.map((connector) => <Polyline
            key={connector.key}
            path={connector.path}
            options={{
              strokeColor: ROUTE_OFFLINE_COLOR,
              strokeOpacity: connector.fallback ? 0.42 : 0.72,
              strokeWeight: 6,
              clickable: false,
              zIndex: 4,
              geodesic: Boolean(connector.fallback),
            }}
          />) : null}
        {viewMode === "route" && routeVisualSegments.length ? routeVisualSegments.map((segment) => <Polyline
            key={segment.key}
            path={segment.path}
            options={{
              strokeColor: ROUTE_NORMAL_COLOR,
              strokeOpacity: 0.92,
              strokeWeight: 5,
              clickable: false,
              zIndex: 5,
            }}
          />) : null}
        {viewMode === "route" && routeVisualSpeedingConnectors.length ? routeVisualSpeedingConnectors.map((connector) => <Polyline
            key={connector.key}
            path={connector.path}
            options={{
              strokeColor: ROUTE_SPEEDING_COLOR,
              strokeOpacity: 0.92,
              strokeWeight: 6,
              clickable: false,
              zIndex: 6,
            }}
          />) : null}
        {viewMode === "route" && showRouteDebugDots && routeVisualDots.length ? routeVisualDots.map((dot) => <Marker
            key={dot.key}
            position={dot.position}
            icon={{ url: routeDotIcons[dot.type] || routeDotIcons.normal }}
            title={dot.type === "offline" ? "Offline route point" : dot.type === "speeding" ? "Speeding route point" : "Connected route point"}
            zIndex={dot.type === "normal" ? 8 : 12}
          />) : null}
        {viewMode === "track" && livePosition ? <Marker position={livePosition} icon={{ url: liveIcon }} title="Live vehicle position" /> : null}
        {mapInfringementRecords.slice(0, 30).map((record, index) => {
          if (!record?.position) return null;
          return <Marker
            key={record.id}
            position={record.position}
            icon={{ url: record?.type === "offline" ? offlineIcon : speedingIcon }}
            title={record?.type === "offline" ? `Offline infringement ${index + 1}` : `Speeding infringement ${index + 1}`}
          />;
        })}
      </GoogleMap>
      {mapNotice ? <div className="absolute left-4 top-4 z-10 max-w-[min(440px,calc(100%-2rem))] rounded-2xl border border-[#f1dcc9] bg-white/95 px-4 py-3 text-sm leading-6 text-[#4f4037] shadow-[0_12px_30px_rgba(15,23,42,0.10)]">
        <div className="font-semibold text-[#211c17]">Live position pending</div>
        <div>{mapNotice}</div>
      </div> : null}
      <div className="absolute bottom-4 left-4 z-10 rounded-xl border border-[#efd7c2] bg-white/95 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#7a695b] shadow-[0_10px_25px_rgba(15,23,42,0.08)]">
        <div>Zoom {zoomLevel}</div>
        <div className="mt-1 text-[10px] text-[#9a8473]">
          {viewMode === "track"
            ? hasLiveTelemetry
              ? `${speedingRecords.length} speeding alerts`
              : "Planned route context"
            : routePointCount >= 2
            ? `${routePointCount} route points`
            : "Waiting for route samples"}
        </div>
      </div>
    </div>
      </section>
    </div>
  </div>;
};
